// The digest of what a lab report was READ as, for one value (TGCLOUD-1868, plan B1, D3). Pure: no
// React, no requests.
//
// A value the reader is not confident of arrives in the verification view EMPTY, to be typed as the
// report prints it or chosen from a list. The answer's proposal does not say what was read; it
// carries this digest (`lowConfidence[].readingDigest`, and for a c., p. or g. description the
// reader normalised, `printedDigest`, of what the report prints). The view digests what was
// entered the same way and compares: "Matches what was read", or "Differs from what was read.
// Check the report." The save compares again on the server, from the signed reading.
//
// The token beside the answer keeps the whole reading, SEALED (AES-256-GCM, its claims compressed
// first: backend intakeToken.js; decision 11), so nothing in the browser can read it, and nothing in
// the view may try to fill or check a field from it: the empty field is a control on what the view
// shows (hazard 1868-H1), and only this code holds it.
//
//   digestOf(runId, path, value)  SHA-256, lower-case hex, of
//                                 runId + '\u0000' + path + '\u0000' + canonical(path, value)
//
// CANONICAL is what two spellings of the same value have in common, by the path's last segment:
//   variantC, variantP, variantG, transcript   every whitespace character removed
//   zygosity, inheritance, genomeBuild,        the CODE: a string as it is; an object's `value`,
//   variantInterpretation, testResult,         else its `label`
//   testResultSuggestion
//   anything else                              the text
// then, for all but the HGVS and transcript paths, NFC, trimmed, and each run of whitespace one
// space. Case is kept.
//
// THE SERVER'S TWIN is backend/services/testResults/readingDigest.js. One vectors file,
// backend/__tests__/fixtures/readingDigestVectors.json, is run by readingDigest.test.js here and by
// its backend namesake, so the two cannot drift.

const SEPARATOR = '\u0000';
// Exported so readingDigest.test.js holds them to the server's lists, entry for entry.
export const NO_WHITESPACE = Object.freeze([
  'variantC',
  'variantP',
  'variantG',
  'transcript'
]);
export const CODED = Object.freeze([
  'zygosity',
  'inheritance',
  'genomeBuild',
  'variantInterpretation',
  'testResult',
  'testResultSuggestion'
]);

const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const lastSegmentOf = (path) => {
  const text = typeof path === 'string' ? path : '';
  return text.slice(text.lastIndexOf('.') + 1);
};

function textOf(value) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function codeOf(value) {
  if (isPlain(value)) {
    if (value.value !== undefined && value.value !== null) {
      return textOf(value.value);
    }
    return textOf(value.label);
  }
  return textOf(value);
}

/** What two spellings of the same value at `path` have in common (see the header). */
export function canonical(path, value) {
  const last = lastSegmentOf(path);
  const text = (CODED.includes(last) ? codeOf(value) : textOf(value)).normalize(
    'NFC'
  );
  if (NO_WHITESPACE.includes(last)) return text.replace(/\s+/g, '');
  return text.trim().replace(/\s+/g, ' ');
}

const hex = (buffer) =>
  Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

/**
 * The digest of `value` read at `path` in run `runId` (see the header), with WebCrypto.
 *
 * @returns {Promise<string>} 64 lower-case hex characters
 */
export async function digestOf(runId, path, value) {
  const subtle = globalThis.crypto && globalThis.crypto.subtle;
  if (!subtle) {
    throw new Error('This browser cannot compare a value with the reading.');
  }
  const input = `${textOf(runId)}${SEPARATOR}${textOf(
    path
  )}${SEPARATOR}${canonical(path, value)}`;
  return hex(await subtle.digest('SHA-256', new TextEncoder().encode(input)));
}

/**
 * Whether what was entered matches what was read: true, false, or null when there is nothing read
 * to compare it with (`readingDigest` null) or nothing entered. A c., p. or g. description matches
 * the reading as the reader normalised it, or what the report prints (`printedDigest`, when the
 * answer has one): the view asks for it as printed. The server's twin is readingDigest.matchesRead.
 *
 * @returns {Promise<boolean|null>}
 */
export async function matchesReading({
  runId,
  path,
  value,
  readingDigest,
  printedDigest = null
}) {
  if (typeof readingDigest !== 'string' || !readingDigest) return null;
  if (canonical(path, value) === '') return null;
  const digest = await digestOf(runId, path, value);
  return (
    digest === readingDigest ||
    (typeof printedDigest === 'string' &&
      !!printedDigest &&
      digest === printedDigest)
  );
}
